<template>
  <v-card class="pa-3 mb-4">
    <v-row style="max-width: 100%; overflow-x: auto; display: flex; flex-wrap: nowrap">
      <v-col cols="auto"> <meta-data-filter /></v-col>
      <v-col cols="auto"> <amount-filter /></v-col>
      <v-col cols="auto"> <created-filter store-type="payment" /></v-col>
      <v-col cols="auto">
        <status-filter
          :status-list="statusOptions"
          :translator="statusTranslator"
          :lock-to-active-inactive="false"
        />
      </v-col>
      <v-col :class="customerPage ? 'd-none' : ''" cols="auto"> <customer-filter /></v-col>

      <v-col class="d-flex justify-end">
        <v-btn
          variant="text"
          color="primary"
          class="mr-3"
          v-if="Object.keys(filters)?.length > 0"
          @click="clearAllFilter()"
        >
          {{ $t("page.payment.filter.clearFilters") }}
        </v-btn>
        <v-tooltip location="top">
          <template v-slot:activator="{ props }">
            <v-btn
              v-if="
                !customerPage &&
                hasRoleAccessToAction([
                  UserRoleStatus.ADMIN,
                  UserRoleStatus.OWNER,
                  UserRoleStatus.STAFF,
                ])
              "
              v-bind="props"
              prepend-icon="mdi-file-chart-outline"
              @click="goToReports"
            >
              {{ $t("navBar.reports") }}
              <v-chip class="ml-2" color="success" size="x-small" variant="flat">New</v-chip>
            </v-btn>
          </template>
          <span>{{ $t("page.payment.reportsButtonTooltip") }}</span>
        </v-tooltip>
      </v-col>
    </v-row>
  </v-card>
</template>

<script lang="ts" setup>
import MetaDataFilter from "@/shared/components/Common/SearchFilter/MetaDataFilter.vue";
import AmountFilter from "@/shared/components/Common/SearchFilter/AmountFilter.vue";
import CreatedFilter from "@/shared/components/Common/SearchFilter/CreatedFilter.vue";
import StatusFilter from "@/shared/components/Common/SearchFilter/StatusFilter.vue";
import { ref, computed, reactive } from "vue";
import { usePaymentStore } from "@/payments/stores/payments";
import CustomerFilter from "@/shared/components/Common/CustomerFilter.vue";
import { useRoute, useRouter } from "vue-router";
import { hasRoleAccessToAction } from "@/shared/utils/role.utils";
import { PaymentStatus, UserRoleStatus } from "@/shared/enums/status.enum";
import i18n from "@/app/plugins/i18n";

const currentRoute = useRoute();
const router = useRouter();
const filters = computed(() => usePaymentStore().filterPaymentCondition || null);
// Only actual PaymentIntent states and the existing refund-derived filters are
// searchable here; shared display enums also contain non-PaymentIntent aliases.
const statusOptions = computed(() =>
  [
    PaymentStatus.SUCCEEDED,
    PaymentStatus.CANCELED,
    PaymentStatus.REQUIRES_PAYMENT_METHOD,
    PaymentStatus.REQUIRES_CONFIRMATION,
    PaymentStatus.REQUIRES_ACTION,
    PaymentStatus.PROCESSING,
    PaymentStatus.REQUIRES_CAPTURE,
    PaymentStatus.REFUNDED,
    PaymentStatus.PARTIALLY_REFUNDED,
  ].map((value) => ({ title: i18n.global.t(`status.${value}`), value })),
);
const statusTranslator = {
  toStore: (value: string) => value || undefined,
  fromStore: (value: unknown) => (typeof value === "string" ? value : ""),
  getLabel: (value: string) =>
    statusOptions.value.find((option) => option.value === value)?.title || "",
};
const goToReports = () => {
  void router.push({ name: "reports" });
};
const customerPage = ref(currentRoute.params?.id?.includes("cus_"));

const clearAllFilter = () => {
  const filterPaymentCondition = reactive(usePaymentStore().filterPaymentCondition);
  delete filterPaymentCondition.status;
  delete filterPaymentCondition.minAmount;
  delete filterPaymentCondition.maxAmount;
  delete filterPaymentCondition.startDate;
  delete filterPaymentCondition.endDate;
  delete filterPaymentCondition.location;
  delete filterPaymentCondition.terminal;
  delete filterPaymentCondition.productId;
  delete filterPaymentCondition.priceId;
  delete filterPaymentCondition.paymentLink;

  if (!customerPage.value) {
    delete filterPaymentCondition.customer;
  }

  usePaymentStore().setClearAllFilter(true);
};
</script>
